import React, { useMemo, useState } from "react";
import { useNotaire } from "../context/NotaireContext";
import EmptyState from "../components/EmptyState";
import { Link } from "react-router-dom";
import { Heading } from "../components/catalyst/heading";
import {
  CalendarDateRangeIcon,
  CalendarDaysIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import TypeAffaireBadge from "../components/TypeAffaireBadge";
import Pagination from "../components/Pagination";

/* ===================== Helpers dates ===================== */

// fields.date = "dd/MM" (texte) + fields.date_creation = date Airtable (ISO)
// On reconstruit une Date UTC (minuit) pour comparer par "jour".
function parseDayOnly(fields) {
  if (!fields?.date) return null;

  const [ddStr, mmStr] = String(fields.date).split("/");
  const dd = parseInt(ddStr, 10);
  const mm = parseInt(mmStr, 10);
  if (!dd || !mm) return null;

  const created = fields?.date_creation ? new Date(fields.date_creation) : new Date();
  if (Number.isNaN(created.getTime())) return null;

  const year = created.getFullYear();

  let rdv = new Date(Date.UTC(year, mm - 1, dd, 0, 0, 0, 0));

  const createdDay = new Date(
    Date.UTC(created.getFullYear(), created.getMonth(), created.getDate(), 0, 0, 0, 0)
  );

  if (rdv.getTime() < createdDay.getTime()) {
    rdv = new Date(Date.UTC(year + 1, mm - 1, dd, 0, 0, 0, 0));
  }

  return rdv;
}

function todayDayOnly() {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
}

/* ===================== Add-to-calendar (Google / Outlook web) ===================== */

function pad2(n) {
  return String(n).padStart(2, "0");
}

function googleDateUTC(d) {
  return (
    d.getUTCFullYear().toString() +
    pad2(d.getUTCMonth() + 1) +
    pad2(d.getUTCDate()) +
    "T" +
    pad2(d.getUTCHours()) +
    pad2(d.getUTCMinutes()) +
    pad2(d.getUTCSeconds()) +
    "Z"
  );
}

function extractHour(value, fallback = 9) {
  if (value === undefined || value === null || value === "") return fallback;

  const str = String(value).trim().toLowerCase();

  if (/^\d+$/.test(str)) return Number(str);

  const match = str.match(/^(\d{1,2})(?:[:h](\d{2}))?$/);
  if (!match) return fallback;

  const hours = Number(match[1]);
  return Number.isNaN(hours) ? fallback : hours;
}

function extractMinutes(value, fallback = 0) {
  if (value === undefined || value === null || value === "") return fallback;

  const str = String(value).trim().toLowerCase();
  const match = str.match(/^(\d{1,2})(?:[:h](\d{2}))?$/);
  if (!match || !match[2]) return fallback;

  const minutes = Number(match[2]);
  return Number.isNaN(minutes) ? fallback : minutes;
}

// Construit les Date en heure locale (navigateur) à partir du texte "dd/MM"
// et des champs heure_debut / heure_fin qui peuvent être "9", "09", "9h", "09:30", etc.
function parseRdvDateRange(fields) {
  const dayUTC = parseDayOnly(fields);
  if (!dayUTC) return null;

  const year = dayUTC.getUTCFullYear();
  const month = dayUTC.getUTCMonth();
  const day = dayUTC.getUTCDate();

  const startHour = extractHour(fields?.heure_debut, 9);
  const startMinute = extractMinutes(fields?.heure_debut, 0);

  const defaultEndHour = startHour + 1;
  const endHour = extractHour(fields?.heure_fin, defaultEndHour);
  const endMinute = extractMinutes(fields?.heure_fin, 0);

  const startLocal = new Date(year, month, day, startHour, startMinute, 0, 0);
  const endLocal = new Date(year, month, day, endHour, endMinute, 0, 0);

  if (Number.isNaN(startLocal.getTime()) || Number.isNaN(endLocal.getTime())) {
    return null;
  }

  if (endLocal <= startLocal) {
    endLocal.setHours(startLocal.getHours() + 1, startLocal.getMinutes(), 0, 0);
  }

  return { startLocal, endLocal };
}

function formatLocalISONoZ(d) {
  return (
    d.getFullYear() +
    "-" +
    pad2(d.getMonth() + 1) +
    "-" +
    pad2(d.getDate()) +
    "T" +
    pad2(d.getHours()) +
    ":" +
    pad2(d.getMinutes())
  );
}

function libelleAffaire(fields) {
  switch (fields?.type_affaire) {
    case "mariage":
      return "contrat mariage";
    case "pacs":
      return "pacs";
    case "pma":
      return "pma";
    case "achat":
      return "achat immo";
    case "vente":
      return "vente immo";
    case "certification":
      return "certification";
    case "divorce":
      return "divorce";
    case "donation":
      return "donation";
    case "bail":
      return "bail";
    default:
      return (fields?.type_affaire || "").toLowerCase();
  }
}

function libelleAffaireLong(fields) {
  switch (fields?.type_affaire) {
    case "mariage":
      return "Contrat de mariage";
    case "pacs":
      return "PACS";
    case "pma":
      return "PMA";
    case "achat":
      return "Achat immobilier";
    case "vente":
      return "Vente immobilière";
    case "certification":
      return "Certification";
    case "divorce":
      return "Divorce";
    case "donation":
      return "Donation";
    case "bail":
      return "Bail";
    default:
      return fields?.type_affaire || "";
  }
}

function libelleTypeRdv(fields) {
  switch (fields?.type_rdv) {
    case "telephone":
      return "par Téléphone";
    case "presentiel":
      return "à l'étude";
    case "visio":
      return "en Visio";
    default:
      return "";
  }
}

function safeStr(v) {
  if (Array.isArray(v)) v = v[0];
  return v === undefined || v === null ? "" : String(v);
}

function buildCalendarLinks(fields) {
  const range = parseRdvDateRange(fields);
  if (!range) return {};

  const { startLocal, endLocal } = range;

  if (
    Number.isNaN(startLocal.getTime()) ||
    Number.isNaN(endLocal.getTime()) ||
    endLocal <= startLocal
  ) {
    return {};
  }

  const nom = safeStr(fields?.nom_from_user).toUpperCase().trim();
  const prenom = safeStr(fields?.prenom_from_user).trim();
  const client = `${prenom} ${nom}`.trim();

  const title = `RDV client ${client} - ${libelleAffaire(fields)} - Clotere`.trim();
  const details = `Rendez-vous Clotere - Client ${client} - ${libelleTypeRdv(fields)} - ${libelleAffaireLong(fields)}`.trim();

  const location =
    fields?.type_rdv === "presentiel"
      ? "À l'étude du notaire"
      : fields?.type_rdv === "telephone"
        ? "Par téléphone"
        : fields?.type_rdv === "visio"
          ? "En visioconférence"
          : "Non précisé";

  const gDates = `${googleDateUTC(startLocal)}/${googleDateUTC(endLocal)}`;
  const google =
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${encodeURIComponent(title)}` +
    `&dates=${encodeURIComponent(gDates)}` +
    `&details=${encodeURIComponent(details)}` +
    `&location=${encodeURIComponent(location)}` +
    `&ctz=${encodeURIComponent("Europe/Paris")}`;

  const outlookStart = formatLocalISONoZ(startLocal);
  const outlookEnd = formatLocalISONoZ(endLocal);
  const outlook =
    `https://outlook.live.com/calendar/0/deeplink/compose?allday=false` +
    `&subject=${encodeURIComponent(title)}` +
    `&body=${encodeURIComponent(details)}` +
    `&location=${encodeURIComponent(location)}` +
    `&startdt=${encodeURIComponent(outlookStart)}` +
    `&enddt=${encodeURIComponent(outlookEnd)}`;

  return { google, outlook, title, details };
}

/* ===================== Badges ===================== */

function StatutBadge({ statut }) {
  if (statut === "valide")
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800">
        Validée
      </span>
    );
  if (statut === "attente validation")
    return (
      <span className="inline-flex items-center rounded-md bg-orange-100 px-2 py-1 text-xs font-bold text-orange-800">
        A valider
      </span>
    );
  if (statut === "annule")
    return (
      <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-1 text-xs font-bold text-red-800">
        Annulé
      </span>
    );
  return null;
}

/* ===================== Composant principal ===================== */

const RendezVous = () => {
  const context = useNotaire();
  const demandes = Array.isArray(context?.demandeRdv) ? context.demandeRdv : [];

  const { upcoming, past } = useMemo(() => {
    const today = todayDayOnly();
    const up = [];
    const pa = [];

    for (const rec of demandes) {
      const fields = rec?.fields || {};

      if (String(fields?.statut || "").toLowerCase() === "annule") {
        continue;
      }

      if (String(fields?.statut || "").toLowerCase() === "a verifier") {
        continue;
      }

      const day = parseDayOnly(fields);
      if (!day) continue;

      if (day.getTime() >= today.getTime()) up.push(rec);
      else pa.push(rec);
    }

    up.sort((a, b) => {
      const aStatut = String(a?.fields?.statut || "").toLowerCase();
      const bStatut = String(b?.fields?.statut || "").toLowerCase();

      const aNotValidated = aStatut !== "valide";
      const bNotValidated = bStatut !== "valide";

      if (aNotValidated !== bNotValidated) {
        return aNotValidated ? -1 : 1;
      }

      const aDay = parseDayOnly(a.fields);
      const bDay = parseDayOnly(b.fields);
      const aTime = aDay ? aDay.getTime() : Number.POSITIVE_INFINITY;
      const bTime = bDay ? bDay.getTime() : Number.POSITIVE_INFINITY;

      return aTime - bTime;
    });

    pa.sort((a, b) => {
      const aTime = parseDayOnly(a.fields)?.getTime() ?? 0;
      const bTime = parseDayOnly(b.fields)?.getTime() ?? 0;
      return bTime - aTime;
    });

    return { upcoming: up, past: pa };
  }, [demandes]);

  const [pastPage, setPastPage] = useState(1);
  const pastPageSize = 5;
  const totalPastPages = Math.max(1, Math.ceil(past.length / pastPageSize));

  const currentPastPage = Math.min(pastPage, totalPastPages);
  const pastStartIndex = (currentPastPage - 1) * pastPageSize;
  const pastEndIndex = pastStartIndex + pastPageSize;
  const paginatedPast = past.slice(pastStartIndex, pastEndIndex);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLinks, setModalLinks] = useState({
    google: "",
    outlook: "",
    title: "",
    details: "",
  });

  const openCalendarModal = (fields) => {
    const links = buildCalendarLinks(fields);

    if (!links.google && !links.outlook) {
      console.error("Impossible de générer les liens calendrier", fields);
      return;
    }

    setModalLinks(links);
    setModalOpen(true);
  };

  if (!context) return null;

  return (
    <div className="container">
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Vos rendez-vous</Heading>
      </div>

      <div className="mt-10" />

      <section>
        <h3 className="mb-3 text-base font-semibold text-gray-900">
          Rendez-vous à venir
        </h3>

        {upcoming.length === 0 ? (
          <div className="mt-3 overflow-hidden rounded-lg bg-white text-center shadow-sm">
            <EmptyState
              icon={CalendarDaysIcon}
              title="Aucun rendez-vous à venir"
              description="Lorsqu'un rendez-vous sera planifié, vous serez averti par email."
            />
          </div>
        ) : (
          <div className="mt-4 flow-root">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                        Date
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Type rdv
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Catégorie
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Client
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Statut
                      </th>
                      <th className="py-3.5 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-0">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {upcoming.map(({ id, fields }) => {
                      const isValide = fields?.statut === "valide";

                      let affaireRoute = null;
                      if (fields.type_affaire === "pacs") affaireRoute = "pacs";
                      else if (fields.type_affaire === "mariage") affaireRoute = "mariage";
                      else if (fields.type_affaire === "achat" || fields.type_affaire === "vente") affaireRoute = "immo";
                      else if (fields.type_affaire === "pma") affaireRoute = "pma";
                      else if (fields.type_affaire === "certification") affaireRoute = "certification";
                      else if (fields.type_affaire === "donation") affaireRoute = "donation";
                      else if (fields.type_affaire === "divorce") affaireRoute = "divorce";
                      else if (fields.type_affaire === "bail") affaireRoute = "bail";
                      else if (fields.type_affaire === "succession") affaireRoute = "succession";

                      return (
                        <tr key={id}>
                          <td className="items-center whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-0">
                            <div className="flex items-start">
                              <CalendarDateRangeIcon className="ml-4 size-4 text-slate-400" />
                              <div className="ml-2 font-bold text-gray-900">
                                {fields.date} <br />
                                {fields.heure_debut} à {fields.heure_fin}
                              </div>
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            {fields.type_rdv === "presentiel" && <>Cabinet</>}
                            {fields.type_rdv === "telephone" && <>Téléphone</>}
                            {fields.type_rdv === "visio" && <>Visio</>}
                            {!fields.type_rdv && <>Non renseigné</>}
                            {fields.adresse && (
                              <>
                                <br />
                                {fields.adresse}
                              </>
                            )}
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <TypeAffaireBadge type={fields.type_affaire} />
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <div className="flex items-center justify-end gap-x-2 capitalize sm:justify-start">
                              {fields.nom_from_user} {fields.prenom_from_user}
                            </div>
                            {affaireRoute && (
                              <Link
                                className="underline"
                                to={`/app/affaire/${affaireRoute}/${fields.id_affaire}`}
                              >
                                <small>Voir l'affaire</small>
                              </Link>
                            )}
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <div className="flex items-center justify-end gap-x-2 sm:justify-start">
                              <StatutBadge statut={fields.statut} />
                            </div>
                          </td>

                          <td className="flex flex-row justify-end gap-2 whitespace-nowrap py-3 text-sm text-gray-500">
                            <>
                              <Link
                                to={`/rdv/${id}`}
                                target="_blank"
                                className="btn-secondary rounded-full"
                              >
                                Modifier
                              </Link>

                              {!isValide && (
                                <Link to={`/rdv/${id}`} target="_blank" className="btn-main">
                                  Valider
                                </Link>
                              )}

                              {isValide && (
                                <button
                                  type="button"
                                  onClick={() => openCalendarModal(fields)}
                                  className="btn-secondary inline-flex items-center gap-2 rounded-full"
                                  title="Ajouter à votre agenda"
                                >
                                  <CalendarIcon className="size-5" />
                                </button>
                              )}
                            </>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="mt-12">
        <h3 className="mb-3 text-base font-semibold text-gray-900">
          Rendez-vous passés
        </h3>

        {past.length === 0 ? (
          <div className="mt-3 overflow-hidden rounded-lg bg-white text-center shadow-sm">
            <EmptyState
              icon={CalendarDaysIcon}
              title="Aucun rendez-vous passé"
              description="Les rendez-vous passés apparaîtront ici."
            />
          </div>
        ) : (
          <>
            <div className="mt-4 flow-root">
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead>
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                          Date
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Type rdv
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Catégorie
                        </th>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Client
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 bg-white">
                      {paginatedPast.map(({ id, fields }) => (
                        <tr key={id}>
                          <td className="items-center whitespace-nowrap py-3 pl-4 pr-3 text-sm sm:pl-0">
                            <div className="flex items-start">
                              <CalendarDateRangeIcon className="ml-4 size-4 text-slate-400" />
                              <div className="ml-2 font-bold text-gray-900">
                                {fields.date} <br />
                                {fields.heure_debut} à {fields.heure_fin}
                              </div>
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            {fields.type_rdv === "presentiel" && <>Cabinet</>}
                            {fields.type_rdv === "telephone" && <>Téléphone</>}
                            {fields.type_rdv === "visio" && <>Visio</>}
                            {!fields.type_rdv && <>Non renseigné</>}
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <TypeAffaireBadge type={fields.type_affaire} />
                          </td>

                          <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                            <div className="flex items-center justify-end gap-x-2 capitalize sm:justify-start">
                              {fields.nom_from_user} {fields.prenom_from_user}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <Pagination
              currentPage={currentPastPage}
              totalPages={totalPastPages}
              onPageChange={setPastPage}
            />
          </>
        )}
      </section>

      <Dialog open={modalOpen} onClose={setModalOpen} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-gray-900/50"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-sm data-closed:sm:translate-y-0 data-closed:sm:scale-95 dark:bg-gray-800 dark:outline dark:-outline-offset-1 dark:outline-white/10"
            >
              <div className="text-center">
                <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-500/10">
                  <CalendarIcon className="size-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900 dark:text-white"
                >
                  Ajouter à votre agenda
                </DialogTitle>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Choisissez votre agenda pour ajouter ce rendez-vous.
                </p>

                <div className="mt-5 grid grid-cols-1 gap-3">
                  {modalLinks.outlook && (
                    <a
                      href={modalLinks.outlook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                      Ajouter dans Outlook
                    </a>
                  )}

                  {modalLinks.google && (
                    <a
                      href={modalLinks.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full justify-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                    >
                      Ajouter dans Google Agenda
                    </a>
                  )}

                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RendezVous;